import $ from "jquery";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import * as envelope from "envelope";
import * as keyboardShortcuts from "keyboardShortcuts";
import readlangClient from "readlangClient";
import type BookStore from "stores/BookStore";
import type { AudioMapPoint } from "stores/BookStore";
import type ReaderTextStore from "stores/ReaderTextStore";
import ReaderViewStore from "stores/ReaderViewStore";
import type { SyncAudioClickMode } from "SyncAudioControls";
import { submitTranscriptWithConfirmation } from "transcriptSubmission";
import * as videoPlayer from "videoPlayer";
import type { ApplyTranscriptMode } from "views/ApplyTranscriptDialog";

export interface SyncAudioControllerOptions {
  appRouter: any;
  el: HTMLElement | JQuery<HTMLElement> | string;
  gotoPosition: (position: number) => void;
  hideFooter: () => void;
  isOwner: () => boolean;
  markAsCompleted: () => void;
  model: BookStore;
  playerElement?: HTMLElement;
  /**
   * Getter rather than a captured instance: a fresh ReaderTextStore is
   * created per page load, so the controller must always talk to (and
   * re-register its click handlers on, via bindTextStore) the current one.
   */
  getReaderTextStore: () => ReaderTextStore | undefined;
  readerViewStore: ReaderViewStore;
  scroll?: boolean;
  setMediaPanelHeight: (height: number) => void;
  enterScrollMode: () => void;
  showControls: () => void;
  showFooter: () => void;
  startPosition?: number;
  textDirection: "LTR" | "RTL";
}

interface UpdateAudioPositionOptions {
  currentTime?: number;
  animateTime?: number;
  scroll?: boolean;
}

const getElement = (
  element: HTMLElement | JQuery<HTMLElement> | string
): HTMLElement => {
  if (typeof element === "string") {
    const matchedElement = $(element)[0];
    if (!matchedElement) {
      throw new Error(`Could not find SyncAudioController element: ${element}`);
    }
    return matchedElement;
  }

  if (element instanceof HTMLElement) {
    return element;
  }

  const jqueryElement = element as JQuery<HTMLElement>;
  if (!jqueryElement[0]) {
    throw new Error("SyncAudioController requires a non-empty element");
  }
  return jqueryElement[0];
};

const currentWordPosition = (
  audioMap: AudioMapPoint[] | undefined,
  currentAudioTime: number
) => Math.round(envelope.getWord(audioMap, currentAudioTime));

export default class SyncAudioController {
  readonly el: HTMLElement;
  readonly $el: JQuery<HTMLElement>;

  private audioMapToSave?: AudioMapPoint[];
  private blocked = false;
  private body = $("body");
  private currentAudioTime = 0;
  private disabled = false;
  private disposeReaderViewEffect?: () => void;
  private endedAnimationInterval?: number;
  private mediaPlayer?: typeof videoPlayer;
  private options: SyncAudioControllerOptions;
  private playerElement?: JQuery<HTMLElement>;
  private saveTimer?: number;
  private started = false;

  private readonly gotoClickHandler = (position: number) => {
    this.gotoWord(position);
  };

  private readonly syncClickHandler = (position: number) => {
    const newPoint = {
      t: Math.round(this.currentAudioTime * 100) / 100,
      w: position,
    };
    const audioMap = [...(this.model.book.value.audioMap ?? [])];
    envelope.addPoint(audioMap, newPoint);

    this.model.book.value = { ...this.model.book.value, audioMap };
    this.saveSyncPoint(audioMap);
    this.updateAudioPosition({ animateTime: 200 });
    this.highlightSyncWords();
  };

  constructor(options: SyncAudioControllerOptions) {
    this.options = options;
    this.el = getElement(options.el);
    this.$el = $(this.el);

    videoPlayer.reset();
    if (this.model.book.value.youTubeID) {
      this.mediaPlayer = videoPlayer;
    }

    if (!this.mediaPlayer) {
      $("body").removeClass("syncAudio");
      return;
    }

    this.initializeMediaMode();
  }

  private get model() {
    return this.options.model;
  }

  removeYouTube() {
    readlangClient.request(
      "DELETE",
      "/api/book/" + this.model.id + "/youTubeID",
      {},
      () => {
        window.location.href = "/library/" + this.model.id;
      },
      (data) => {
        console.error("error deleting YouTubeID", data);
      }
    );
  }

  saveYouTubeID(userInput: string) {
    const idOnlyRegExp = /^[a-zA-Z0-9_-]{11}$/;
    // cspell: disable-next-line
    const idInURLRegExp = /(v=|youtu\.be\/|\/live\/)([a-zA-Z0-9_-]{11})/;
    let newID;

    if (idOnlyRegExp.test(userInput)) {
      newID = userInput;
    } else {
      const match = idInURLRegExp.exec(userInput);
      if (match && match[2]) {
        newID = match[2];
      }
    }

    if (!newID) {
      alert("YouTube URL or ID not valid. The ID must be 11 characters.");
      return;
    }

    void this.model
      .patchBook({ youTubeID: newID })
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.error("error saving YouTubeID", error);
        if (!error?.jqXHR?.alreadyNotified) {
          alert("Couldn't save the YouTube video. Please try again.");
        }
      });
  }

  async submitYouTubeTranscript(
    transcript: string,
    mode: ApplyTranscriptMode
  ): Promise<{ textReplaced: boolean }> {
    const responseData = await submitTranscriptWithConfirmation({
      confirmReplace: (message) => confirm(message),
      submit: (force) =>
        readlangClient.request(
          "POST",
          `/api/book/${this.model.id}/addTimings`,
          {
            transcript,
            replaceText: mode === "replace-text",
            force,
          }
        ),
    });

    if (!responseData) {
      // User declined to replace their existing sync points
      return { textReplaced: false };
    }

    if (!responseData.updated) {
      alert(responseData.message ?? "Failed to update transcript.");
      console.error(responseData);
      return { textReplaced: false };
    }

    if (responseData.textReplaced) {
      return { textReplaced: true };
    }

    this.model.book.value = {
      ...this.model.book.value,
      audioMap: responseData.audioMap,
    };
    this.updateAudioPosition({ animateTime: 200 });
    this.highlightSyncWords();
    alert("Transcript timings updated successfully.");
    return { textReplaced: false };
  }

  private initializeMediaMode() {
    this.options.enterScrollMode();
    $("body").addClass("syncAudio");

    this.registerKeyboardShortcuts();

    if (
      !this.model.book.value.audioMap ||
      this.model.book.value.audioMap.length === 0
    ) {
      this.addImpliedSyncPoints([{ t: 0, w: 0 }]);
    }

    this.disposeReaderViewEffect = this.options.readerViewStore.onChange(
      (readerViewStore) => [
        readerViewStore.firstPosition.value,
        readerViewStore.lastPosition.value,
        readerViewStore.fromPosition.value,
      ],
      () => {
        this.updateAudioPosition({});
        this.updatePage();
      }
    );
    this.body = $("body");

    this.options.setMediaPanelHeight(
      Math.max(253, ($(window).height() - 40) / 2)
    );
    this.options.hideFooter();

    if (!this.options.playerElement) {
      throw new Error("SyncAudioController requires a media player element");
    }

    this.playerElement = $(this.options.playerElement);

    this.loadVideo();
    this.bindTextStore();
  }

  private get readerTextStore(): ReaderTextStore | undefined {
    return this.options.getReaderTextStore();
  }

  /** Registers the click-mode handlers on the current text store. Called on
   * construction and again after each page load replaces the store. */
  bindTextStore() {
    this.readerTextStore?.addClickHandler("sync", this.syncClickHandler);
    this.readerTextStore?.addClickHandler("goto", this.gotoClickHandler);
  }

  private registerKeyboardShortcuts() {
    keyboardShortcuts.registerHandler(
      "readerPage",
      " ".charCodeAt(0),
      (event) => {
        event?.preventDefault();
        if (this.mediaPlayer?.status().paused) {
          this.mediaPlayer.play(undefined);
        } else {
          this.mediaPlayer?.pause();
        }
      }
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      83,
      () => {
        this.mediaPlayer?.toggleSpeed();
      },
      true,
      true,
      "ctrl"
    );

    keyboardShortcuts.registerHandler(
      "readerPage",
      " ".charCodeAt(0),
      (event) => {
        event?.preventDefault();
        if (this.mediaPlayer?.status().paused) {
          this.mediaPlayer.play(undefined);
        } else {
          this.mediaPlayer?.pause();
        }
      },
      true,
      true,
      "ctrl"
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      ",".charCodeAt(0),
      () => {
        this.mediaPlayer?.jumpBack(undefined);
      },
      true,
      false,
      "ctrl"
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      ".".charCodeAt(0),
      () => {
        this.mediaPlayer?.jumpForward(undefined);
      },
      true,
      false,
      "ctrl"
    );
  }

  private unregisterKeyboardShortcuts() {
    keyboardShortcuts.unregisterHandler("readerPage", " ".charCodeAt(0));
    keyboardShortcuts.unregisterHandler("readerPage", 83, true, "ctrl");
    keyboardShortcuts.unregisterHandler(
      "readerPage",
      " ".charCodeAt(0),
      true,
      "ctrl"
    );
    keyboardShortcuts.unregisterHandler(
      "readerPage",
      ",".charCodeAt(0),
      false,
      "ctrl"
    );
    keyboardShortcuts.unregisterHandler(
      "readerPage",
      ".".charCodeAt(0),
      false,
      "ctrl"
    );
  }

  private loadVideo() {
    let audioStart = 0;
    if (typeof this.options.startPosition === "number") {
      const buffer = 4;
      audioStart = Math.max(
        0,
        envelope.getTime(
          this.model.book.value.audioMap,
          this.options.startPosition
        ) - buffer
      );

      if (this.options.startPosition === 0 && audioStart < 300 - buffer) {
        audioStart = 0;
      }
    }

    videoPlayer.loadVideo({
      playerElement: this.playerElement,
      youTubeID: this.model.book.value.youTubeID,
      onTimeUpdate: (currentTime) => {
        if (this.disabled) {
          return;
        }
        this.updateAudioPosition({
          currentTime,
          animateTime: 100,
          scroll: this.options.scroll,
        });

        if (deviceDetect.is("iOS") && this.started === false) {
          this.options.showControls();
          this.started = true;
        }
      },
      onReady: (newDuration) => {
        const audioMap = [...(this.model.book.value.audioMap ?? [])];

        if (_.last(audioMap).w < (this.model.book.value.totalWords ?? 0) - 1) {
          envelope.addPoint(audioMap, {
            t: newDuration,
            w: (this.model.book.value.totalWords ?? 1) - 1,
          });
          this.addImpliedSyncPoints(audioMap);
        }
      },
      onEnded: () => {
        if (this.disabled) {
          return;
        }
        this.options.markAsCompleted();
        // Clear any still-running animation from a previous ending so at most
        // one interval exists and clearing via the field is always correct.
        clearInterval(this.endedAnimationInterval);
        let frames = 0;
        this.endedAnimationInterval = window.setInterval(() => {
          this.updateAudioPosition({ scroll: true });
          frames++;
          if (frames > 30) {
            clearInterval(this.endedAnimationInterval);
          }
        }, 50);

        if (
          this.model.book.value.nextBookID &&
          confirm("Play the next chapter?")
        ) {
          this.options.appRouter.navigate(
            "library/" + this.model.book.value.nextBookID + "/scroll/0",
            { trigger: true }
          );
        }
      },
      onBuffering: () => {
        if (deviceDetect.is("iOS") && this.started === false) {
          this.options.showControls();
          this.started = true;
        }
      },
      start: audioStart,
    });
  }

  setClickMode = (clickMode: SyncAudioClickMode) => {
    this.readerTextStore?.setClickMode(
      clickMode === "disabled" ? "translate" : clickMode
    );

    switch (clickMode) {
      case "sync":
        this.highlightSyncWords();
        this.disabled = false;
        break;
      case "goto":
      case "translate":
        this.unhighlightSyncWords();
        this.disabled = false;
        break;
      case "disabled":
        this.unhighlightSyncWords();
        this.readerTextStore?.setAudioPosition(undefined);
        this.disabled = true;
        break;
      default:
    }
  };

  /**
   * Sync points implied by the media itself (its start and end). Every reader
   * needs them, but only the owner may save them to the book.
   */
  private addImpliedSyncPoints(audioMap: AudioMapPoint[]) {
    if (!this.options.isOwner()) {
      this.model.patchLocal({ audioMap });
      return;
    }
    this.model.patchBook({ audioMap }).catch(() => {
      console.error("error saving implied sync points");
    });
  }

  private saveSyncPoint(audioMap: AudioMapPoint[]) {
    if (this.blocked) {
      this.audioMapToSave = audioMap;
      return;
    }

    this.blocked = true;
    void this.model
      .patchBook({ audioMap })
      .then(() => {
        this.saveTimer = window.setTimeout(() => {
          this.blocked = false;
          if (this.audioMapToSave) {
            this.saveSyncPoint(this.audioMapToSave);
            delete this.audioMapToSave;
          }
        }, 500);
      })
      .catch(() => {
        console.error("error saving sync point");
        this.blocked = false;
        if (this.audioMapToSave) {
          this.saveSyncPoint(this.audioMapToSave);
          delete this.audioMapToSave;
        }
      });
  }

  private updatePage() {
    if (this.readerTextStore?.clickMode.value === "sync") {
      this.highlightSyncWords();
    }
  }

  private highlightSyncWords() {
    this.readerTextStore?.setAudioSyncPositions(
      new Set((this.model.book.value.audioMap ?? []).map((point) => point.w))
    );
  }

  private unhighlightSyncWords() {
    this.readerTextStore?.setAudioSyncPositions(new Set());
  }

  /** Seeks the video to the audio-map time for `position` and plays it. */
  gotoWord(position: number) {
    if (!this.mediaPlayer) {
      return;
    }

    this.mediaPlayer.play(
      Math.max(
        0.01,
        envelope.getTime(this.model.book.value.audioMap, position) - 0.5
      )
    );
    this.updateAudioPosition({ animateTime: 200 });
  }

  private scrollTo(yPosition: number) {
    const pageTextViewport = document.getElementById("pageTextViewport");
    if (!pageTextViewport) {
      return;
    }

    pageTextViewport.scrollTop =
      pageTextViewport.scrollTop * 0.85 - yPosition * 0.15;
  }

  updateAudioPosition(options: UpdateAudioPositionOptions = {}) {
    if ("currentTime" in options) {
      this.currentAudioTime = options.currentTime;
    }
    const currentPosition = currentWordPosition(
      this.model.book.value.audioMap,
      this.currentAudioTime
    );

    const readerTextStore = this.readerTextStore;
    if (!readerTextStore) {
      return;
    }
    readerTextStore.setAudioPosition(undefined);
    // Scoped to the reading text: the Explain panel renders its own
    // `span[data-p]` elements, which collide by position
    const currentSpan = document.querySelector<HTMLElement>(
      `#pageText span[data-p="${currentPosition}"]`
    );

    this.options.gotoPosition(currentPosition);

    let newY;

    if (
      !this.options.readerViewStore.hasFirstSpan.value ||
      !this.options.readerViewStore.hasLastSpan.value
    ) {
      return;
    }

    const lastPosition = this.options.readerViewStore.lastPosition.value ?? 0;

    const pageText = document.getElementById("pageText");
    const pageTextViewport = document.getElementById("pageTextViewport");

    if (options.scroll && currentSpan && pageText && pageTextViewport) {
      readerTextStore.setAudioPosition(currentPosition);
      const pageTextRect = pageText.getBoundingClientRect();
      const spanRect = currentSpan.getBoundingClientRect();
      const spanTop = spanRect.top - pageTextRect.top;
      const spanLeft = spanRect.left - pageTextRect.left;
      const viewportHeight = pageTextViewport.getBoundingClientRect().height;
      if (currentPosition === lastPosition) {
        newY = -pageTextRect.height + viewportHeight - 100;
      } else {
        newY = Math.round(-spanTop + viewportHeight / 2);
      }

      const offset =
        (spanRect.height * (spanLeft + spanRect.width / 2)) /
        pageTextRect.width;

      if (this.options.textDirection === "RTL") {
        newY += offset - spanRect.height;
      } else {
        newY -= offset;
      }

      if (readerTextStore.clickMode.value !== "sync") {
        this.scrollTo(newY);
      }
      return;
    }

    if (currentSpan && currentPosition <= lastPosition) {
      readerTextStore.setAudioPosition(currentPosition);
    }
  }

  status() {
    return this.mediaPlayer && this.mediaPlayer.status();
  }

  hasMediaPlayer() {
    return Boolean(this.mediaPlayer);
  }

  updateOptions(newOptions: Partial<SyncAudioControllerOptions>) {
    this.options = { ...this.options, ...newOptions };
  }

  remove() {
    this.mediaPlayer?.disable();
    this.unregisterKeyboardShortcuts();
    this.disposeReaderViewEffect?.();
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    clearInterval(this.endedAnimationInterval);
    this.readerTextStore?.removeClickHandler("sync", this.syncClickHandler);
    this.readerTextStore?.removeClickHandler("goto", this.gotoClickHandler);
    this.$el.off(".syncAudioController");
    this.body.removeClass("syncAudio");
    this.unhighlightSyncWords();
    this.readerTextStore?.setAudioPosition(undefined);
  }
}
